/**
 * ProcessoParceiro Service - API calls
 *
 * Story: 11.5 - Processos Atribuídos
 * Pattern: Axios service layer with error handling
 */
import axios, { AxiosError } from "axios";
import { mensagemDeErro } from "@/lib/erroApi";
import {
  ProcessoListResponse,
  ProcessoParceiro,
  ProcessoFilterParams,
  TimelineListResponse,
  PagamentosByProcessoResponse,
  AddDocumentoRequest,
} from "../types/processo-parceiro";

// Note: VITE_API_URL already includes /api/v1
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token interceptor (uses portal token for partner users)
api.interceptors.request.use((config) => {
  // Portal uses 'portal_access_token' key
  const token = localStorage.getItem("portal_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Error handling helper
const handleApiError = (error: AxiosError<{ detail: string }>) => {
  if (error.response) {
    // Server responded with error. `mensagemDeErro` porque no 422 do FastAPI o `detail` é uma
    // LISTA de objetos, e `new Error(lista)` virava "[object Object]" no aviso (passo 9).
    throw new Error(mensagemDeErro(error, "Erro na requisição"));
  } else if (error.request) {
    // No response received
    throw new Error("Servidor não respondeu. Verifique sua conexão.");
  } else {
    // Request setup error
    throw new Error("Erro ao configurar requisição");
  }
};

/**
 * ProcessoParceiro Service
 */
class ProcessoParceiroService {
  /**
   * Get paginated list of processos with filters
   *
   * @param filters - Filter parameters (status, prazo_vencido, prioridade)
   * @param page - Page number (default: 1)
   * @param pageSize - Items per page (default: 20)
   * @returns ProcessoListResponse with items and pagination
   */
  async getProcessos(
    filters?: ProcessoFilterParams,
    page: number = 1,
    pageSize: number = 20
  ): Promise<ProcessoListResponse> {
    try {
      const params: Record<string, any> = { page, page_size: pageSize };

      if (filters?.status) params.status = filters.status;
      if (filters?.prazo_vencido !== undefined) params.prazo_vencido = filters.prazo_vencido;
      if (filters?.prioridade) params.prioridade = filters.prioridade;
      if (filters?.prazo_ate) params.prazo_ate = filters.prazo_ate;

      const response = await api.get<ProcessoListResponse>("/portal/processos", { params });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<{ detail: string }>);
      throw error; // Unreachable, but TS needs it
    }
  }

  /**
   * Get complete processo details
   *
   * @param processoId - UUID of the processo
   * @returns ProcessoParceiro with all related data
   */
  async getProcessoDetails(processoId: string): Promise<ProcessoParceiro> {
    try {
      const response = await api.get<ProcessoParceiro>(`/portal/processos/${processoId}`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<{ detail: string }>);
      throw error;
    }
  }

  /**
   * Get timeline events for a processo
   *
   * @param processoId - UUID of the processo
   * @param page - Page number (default: 1)
   * @param pageSize - Items per page (default: 50)
   * @returns TimelineListResponse with events
   */
  async getProcessoTimeline(
    processoId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<TimelineListResponse> {
    try {
      const response = await api.get<TimelineListResponse>(
        `/portal/processos/${processoId}/timeline`,
        { params: { page, page_size: pageSize } }
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<{ detail: string }>);
      throw error;
    }
  }

  /**
   * Add document to processo (M2M relationship)
   *
   * @param processoId - UUID of the processo
   * @param request - AddDocumentoRequest with document_id and upload_reason
   * @returns Updated ProcessoParceiro
   */
  async addDocumentoToProcesso(
    processoId: string,
    request: AddDocumentoRequest
  ): Promise<ProcessoParceiro> {
    try {
      const response = await api.post<ProcessoParceiro>(
        `/portal/processos/${processoId}/documentos`,
        request
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<{ detail: string }>);
      throw error;
    }
  }

  /**
   * Get pagamentos for a processo
   *
   * @param processoId - UUID of the processo
   * @returns PagamentosByProcessoResponse with list and totals
   */
  async getProcessoPagamentos(processoId: string): Promise<PagamentosByProcessoResponse> {
    try {
      const response = await api.get<PagamentosByProcessoResponse>(
        `/portal/processos/${processoId}/pagamentos`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<{ detail: string }>);
      throw error;
    }
  }
}

export default new ProcessoParceiroService();
